import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';
import { readWorkingHours, type WorkingHours } from '@/hooks/useClinicUsers';

export interface ClinicMemberOption {
  membership_id: string;
  user_id: string;
  name: string;
  email: string;
  role: Database['public']['Enums']['clinic_role'];
  custom_role_id: string | null;
  working_hours: WorkingHours;
}

export const clinicMembersQueryKey = (clinicId: string | null) =>
  ['clinic-members', clinicId] as const;

export function useClinicMembers(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicMembersQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<ClinicMemberOption[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('clinic_memberships')
        .select(
          'id, user_id, role, status, custom_role_id, working_hours, user:users!clinic_memberships_user_id_fkey(name, email)',
        )
        .eq('clinic_id', clinicId)
        .eq('status', 'active');

      if (error) throw error;

      type Joined = {
        id: string;
        user_id: string;
        role: Database['public']['Enums']['clinic_role'];
        custom_role_id: string | null;
        working_hours: unknown;
        user: { name: string | null; email: string } | null;
      };

      return ((data ?? []) as unknown as Joined[])
        .map(
          (row): ClinicMemberOption => ({
            membership_id: row.id,
            user_id: row.user_id,
            role: row.role,
            custom_role_id: row.custom_role_id,
            working_hours: readWorkingHours(row.working_hours),
            name: row.user?.name ?? row.user?.email ?? 'Unknown',
            email: row.user?.email ?? '',
          }),
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 0,
  });
}
